import React, { useState, useMemo, createContext, useContext } from 'react';
import { DateTime } from 'luxon';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { itemTypes } from '~/modules/common/components/SearchBox/Facets/BillingItemTypeFacet/enhancers/useBillingItemType';
import { useHasFeatureFlag } from '~/modules/common/hooks';
import { useCreateBillingItemFilterFromAvailableToBillFilter } from './hooks/useCreateBillingItemFilterFromAvailableToBillFilter';

export const AddLineItemsDialogContext = createContext();

export const useLineItemsDialogContext = () =>
  useContext(AddLineItemsDialogContext);

export const LineItemsSearchContextProvider = ({
  availableToBillEndDate,
  availableToBillStartDate,
  availableToBillItemType,
  availableToBillProject,
  projectUri,
  projectName,
  children,
  ...props
}) => {
  const isPsaPrpAddProjectColumnOnAvailableToBillEnabled = useHasFeatureFlag({
    featureFlag: 'isPsaPrpAddProjectColumnOnAvailableToBillEnabled'
  });

  const searchState = useCreateBillingItemFilterFromAvailableToBillFilter({
    availableToBillProject,
    availableToBillEndDate,
    availableToBillStartDate,
    availableToBillItemType,
    projectUri,
    projectName
  });
  const intl = useIntl();
  const startDate = availableToBillStartDate
    ? DateTime.fromObject(availableToBillStartDate)
    : null;
  const endDate = availableToBillEndDate
    ? DateTime.fromObject(availableToBillEndDate)
    : null;

  const [searchCriteria, setSearchCriteria] = useState(
    isPsaPrpAddProjectColumnOnAvailableToBillEnabled
      ? searchState
      : {
          keywords: [],
          criterions: {
            'date:':
              startDate && endDate
                ? [
                    {
                      range: {
                        startDate,
                        endDate
                      },
                      key: `(${startDate.toLocaleString(
                        DateTime.DATE_MED
                      )} - ${endDate.toLocaleString(DateTime.DATE_MED)})`
                    }
                  ]
                : [],

            'project:': projectUri
              ? [
                  {
                    value: projectUri,
                    key: projectName,
                    displayText: projectName,
                    label: projectName
                  }
                ]
              : [],

            'itemType:': availableToBillItemType
              ? [
                  {
                    value: availableToBillItemType,
                    key: itemTypes(intl).find(
                      itemType => itemType.id === availableToBillItemType
                    ).label
                  }
                ]
              : []
          }
        }
  );

  const values = useMemo(
    () => ({
      searchCriteria,
      setSearchCriteria,
      ...props
    }),
    [props, searchCriteria]
  );

  return (
    <AddLineItemsDialogContext.Provider value={values}>
      {children}
    </AddLineItemsDialogContext.Provider>
  );
};
LineItemsSearchContextProvider.propTypes = {
  children: PropTypes.node,
  availableToBillProject: PropTypes.object,
  availableToBillEndDate: PropTypes.object,
  availableToBillStartDate: PropTypes.object,
  availableToBillItemType: PropTypes.object,
  projectUri: PropTypes.string,
  projectName: PropTypes.string
};

export default LineItemsSearchContextProvider;
